/**
 * PATH: packages/email-hub/src/feature/provider-writeback/ProviderWritebackControls.tsx
 * FILE_ID: EMAILHUB-PROVIDER-WRITEBACK-CONTROLS-001
 * PURPOSE: Reader receipt status plus Retry and Not Done controls.
 */

import { useState } from 'react'
import { AlertTriangle, CheckCircle2, Loader2, RotateCcw } from 'lucide-react'

import { isEmailHubFeatureEnabled } from '../../_internal/flags'
import type { Email } from '../hooks/useEmails'
import { PROVIDER_WRITEBACK_LABELS } from '../lib/writebackContract'
import { requestProviderRestore, retryProviderWriteback } from './api'

interface Props {
  /** `provider_writeback` is additive-optional on the shared `Email` type
   *  (change #269) — no local intersection type needed here anymore. */
  email: Email
  onChanged?: () => void
}

export function ProviderWritebackControls({ email, onChanged }: Props) {
  const [localState, setState] = useState(email.provider_writeback ?? null)
  const [busy, setBusy] = useState(false)
  const [localError, setLocalError] = useState('')
  const serverState = email.provider_writeback ?? null
  const state =
    localState?.email_id === email.id
    && localState.attempt_count >= (serverState?.attempt_count ?? 0)
      ? localState
      : serverState
  if (!isEmailHubFeatureEnabled('providerWritebackRestore') || !state) return null

  const mutate = async (kind: 'restore' | 'retry') => {
    setBusy(true)
    setLocalError('')
    try {
      const next =
        kind === 'restore'
          ? await requestProviderRestore(email.id)
          : await retryProviderWriteback(state.operation_id)
      setState(next)
      onChanged?.()
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : 'Provider action failed')
    } finally {
      setBusy(false)
    }
  }

  const failed = state.result === 'failed'
  const pending = state.result === 'requested' || state.result === 'running'
  return (
    <div
      className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/30 px-3 py-2 text-xs"
      data-testid="provider-writeback-controls"
    >
      {failed ? (
        <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
      ) : pending ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
      ) : (
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
      )}
      <span>{PROVIDER_WRITEBACK_LABELS[state.result]}</span>
      {state.error_code && (
        <span className="text-destructive" title={state.error_detail}>
          {state.error_code}
        </span>
      )}
      {localError && <span className="text-destructive">{localError}</span>}
      <span className="ml-auto flex items-center gap-1">
        {failed && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void mutate('retry')}
            className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 hover:bg-accent disabled:opacity-50"
          >
            <RotateCcw className="h-3 w-3" />
            Retry
          </button>
        )}
        {state.result === 'completed' && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void mutate('restore')}
            className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 hover:bg-accent disabled:opacity-50"
          >
            <RotateCcw className="h-3 w-3" />
            Not Done
          </button>
        )}
      </span>
    </div>
  )
}
